import { 
  apiGetGames, 
  apiGetGame, 
  apiPutGamePlayed, 
  apiGetGameOfTheWeek, 
  apiGetSearchGames, 
  apiGetPrefilteredGames, 
  apiGetMore 
} from '@/api';
import * as types from '@/store/mutation-types';

import axios from '@/axios.config';
let oldRequestToken = null;

// Models
import GAME from '@/store/models/game';
import PAGINATION from '@/store/models/pagination';

// Services
import { mapEnabled} from '@/store/services/mapEnabled';
import { mapEnabledToSlug } from '@/store/services/mapEnabledToSlug';
import { setEnabled } from '@/store/services/setEnabled';

// Utilities
import { cleanSource } from '@/utilities';

// initial state
const state = {
  filters: {
    order: 'publish_date:desc',
    gameTypes: [],
    themes: [],
    categories: [],
    view: 'card-sm'
  },

  all: {
    data: [],
    meta: PAGINATION
  },

  search: {
    term: '',
    data: [],
    meta: PAGINATION
  },

  gotw: {
    title: '',
    description: '',
    game: GAME,
    images: {
      background: {
        bg_optimized: '',
        original: ''
      },
      heroimage: {
        optimized: '',
        original: ''
      }
    }
  },

  latest: [],

  mostPopular: [],

  prefiltered: []
};

// mutations
const mutations = {
  [types.RECEIVED_LATEST_GAMES] (state, data) {
    state.latest = data.data;
  },

  [types.RECEIVED_MOST_POPULAR_GAMES] (state, data) {
    state.mostPopular = data.data;
  },

  [types.RECEIVED_PREFILTERED_GAMES] (state, data) {
    state.prefiltered = data.data;
  },

  [types.RECEIVED_GAMES] (state, data) {
    state.all.data = data.data;
    state.all.meta = data.meta;
  },

  [types.APPEND_GAMES] (state, data) {
    data.data.forEach(function(obj) {
      state.all.data.push(obj);
    });

    state.all.meta = data.meta;
  },

  [types.RECEIVED_GOTW] (state, data) {
    state.gotw = data;
  },

  [types.RECEIVED_GAMES_GAMETYPES] (state, gameTypes) {
    state.filters.gameTypes = gameTypes;
  },

  [types.UPDATE_GAMES_GAMETYPES] (state, gameType) {
    // find matching object with slug and update enabled state
    setEnabled(state.filters.gameTypes, gameType);
  },

  [types.UPDATE_GAME_THEMES] (state, theme) {
    // find matching object with slug and update enabled state
    setEnabled(state.filters.themes, theme);
  },

  [types.UPDATE_GAME_CATEGORIES] (state, category) {
    // find matching object with slug and update enabled state
    setEnabled(state.filters.categories, category);
  },

  [types.UPDATE_GAMES_ORDER] (state, order) {
    state.filters.order = order;
  },

  [types.DISABLE_ALL_GAMES_FILTERS] (state) {
    state.filters.gameTypes.forEach(el => el.enabled = false);
    state.filters.themes.forEach(el => el.enabled = false);
    state.filters.categories.forEach(el => el.enabled = false);
  },

  [types.RECEIVED_GAME_THEMES] (state, themes) {
    state.filters.themes = themes;
  },

  [types.RECEIVED_GAME_CATEGORIES] (state, categories) {
    state.filters.categories = categories;
  },

  [types.RECEIVED_SEARCH_GAMES] (state, data) {
    state.search.data = data.data;
    state.search.meta = data.meta;
  },

  [types.APPEND_SEARCH_GAMES] (state, data) {
    data.data.forEach(function(obj) {
      state.search.data.push(obj);
    });

    state.search.meta = data.meta;
  },

  [types.SET_GAMES_SEARCH_TERM] (state, term) {
    state.search.term = term;
  },

  [types.CLEAR_SEARCH_GAMES] (state) {
    state.search.data = [];
    state.search.meta = PAGINATION;
  },

  [types.SET_GAMES_VIEW] (state, view) {
    state.filters.view = view;
  }
};

const actions = {
  getLatestGames: ({ commit, state }, args = {}) => {
    // Argument defaults
    args.limit = args.limit || 16;

    // Because we are within the store we need to
    // use Vue.prototype to access the $mq plugin
    if (Vue.prototype.$mq('sm-max')) { 
      args.is_mobile_friendly = true; 
    };
    
    return apiGetGames({ order: 'publish_date:desc', ...args })
      .then(({ data }) => {
        // success
        commit(types.RECEIVED_LATEST_GAMES, data);
      })
      .catch((error) => {
        console.log(error);
      });
  },

  getMostPopularGames: ({ commit, state }, args = {}) => {
    // Argument defaults
    args.limit = args.limit || 16;

    // Because we are within the store we need to
    // use Vue.prototype to access the $mq plugin
    if (Vue.prototype.$mq('sm-max')) { 
      args.is_mobile_friendly = true; 
    };
    
    return apiGetGames({ order: 'popular:desc', include: 'stats', ...args })
      .then(({ data }) => {
        // success
        commit(types.RECEIVED_MOST_POPULAR_GAMES, data);
      })
      .catch((error) => {
        console.log(error);
      });
  },

  getPrefilteredGames: ({ commit, state }, args = {}) => {
    return apiGetPrefilteredGames().then(({ data }) => {
      // success
      commit(types.RECEIVED_PREFILTERED_GAMES, data);
    })
    .catch((error) => {
      console.log(error);
    });
  },

  getAllGames: ({ commit, state }, args = {}) => {
    // Argument defaults
    args.limit = args.limit || 16;

    // Because we are within the store we need to
    // use Vue.prototype to access the $mq plugin
    if (Vue.prototype.$mq('sm-max')) { 
      args.is_mobile_friendly = true; 
    };

    let order = state.filters.order;

    // map game type filters
    // let game_types = mapEnabledToSlug(state.filters.gameTypes);
    let game_types = false;

    if (mapEnabledToSlug(state.filters.gameTypes).length > 0) {
      game_types = mapEnabledToSlug(state.filters.gameTypes);
    } else {
      // If no filters have been selected, manually filter out game_types we do not want
      game_types = [
        {slug: 'videoslots'},
        {slug: 'slots'},
      ];
    }

    let game_themes = mapEnabledToSlug(state.filters.themes);
    let game_categories = mapEnabledToSlug(state.filters.categories);

    // Setup a cancel token so we can cancel any unresolved previous requests
    let token = axios.CancelToken;
    let request = token.source();

    if (oldRequestToken) {
      oldRequestToken.cancel();
      oldRequestToken = null;
    }

    oldRequestToken = request;

    return apiGetGames({ order, game_types, game_themes, game_categories, include: 'stats,suggestedCasinos.casinoGameStats', ...args }, request.token)
      .then(({ data }) => {
        // success
        commit(types.RECEIVED_GAMES, data);
        oldRequestToken = null;
      })
      .catch((error) => {
        console.log(error);
    });
  },

  getMoreGames: ({ commit }, params) => {
    return apiGetMore(params.url).then(({ data }) => {
      commit(types[params.commit], data);
    })
    .catch((error) => {
      console.log(error);
    });
  },

  getGameOfTheWeek: ({ commit }) => {
    return apiGetGameOfTheWeek().then(({ data }) => {
      commit(types.RECEIVED_GOTW, data);
    })
    .catch((error) => {
      console.log(error);
    });
  },

  updateGamesOrder: ({ commit, dispatch }, order) => {
    commit(types.UPDATE_GAMES_ORDER, order);

    dispatch('getAllGames');
  },

  toggleGamesGameTypeFilter: ({ commit, dispatch }, payload) => {
    if (Array.isArray(payload)) {
      payload.forEach((gametype) => {
        commit(types.UPDATE_GAMES_GAMETYPES, gametype);
      });
    } else {
      commit(types.UPDATE_GAMES_GAMETYPES, payload);
    }

    dispatch('getAllGames');
  },

  toggleGamesThemeFilter: ({ commit, dispatch }, theme) => {
    commit(types.UPDATE_GAME_THEMES, theme);

    dispatch('getAllGames');
  },

  toggleGamesCategoryFilter: ({ commit, dispatch }, category) => {
    commit(types.UPDATE_GAME_CATEGORIES, category);

    dispatch('getAllGames');
  },

  disableAllGamesFilters: ({ commit, dispatch }) => {
    commit(types.DISABLE_ALL_GAMES_FILTERS);
    dispatch('addToast', { type: 'success', text: 'All game filters have been removed.' })

    dispatch('getAllGames');
  },

  searchGames: ({ commit }, { term, args = {} }) => {
    // Argument defaults
    args.limit = args.limit || 16;

    // Because we are within the store we need to
    // use Vue.prototype to access the $mq plugin
    if (Vue.prototype.$mq('sm-max')) { 
      args.is_mobile_friendly = true; 
    };

    // Setup a cancel token so we can cancel any unresolved previous searches
    let token = axios.CancelToken;
    let request = token.source();

    if (oldRequestToken) {
      oldRequestToken.cancel();
      oldRequestToken = null;
    }

    oldRequestToken = request;

    commit(types.SET_GAMES_SEARCH_TERM, term);

    return apiGetSearchGames(term, request.token, args)
      .then(({ data }) => {
        commit(types.RECEIVED_SEARCH_GAMES, data);
        oldRequestToken = null;
      })
      .catch((error) => {
        console.log(error);
      });
  },

  clearSearchGames: ({ commit }) => {
    commit(types.SET_GAMES_SEARCH_TERM, '');
    commit(types.CLEAR_SEARCH_GAMES);
  },

  setGamesView: ({ commit }, view) => {
    commit(types.SET_GAMES_VIEW, view);
  }
};

const getters = {
  games (state) {
    return state.all;
  },

  gamesLatest (state) {
    return state.latest;
  },

  gamesMostPopular (state) {
    return state.mostPopular;
  },

  gamesPrefiltered (state) {
    return state.prefiltered;
  },

  gotw (state) {
    return state.gotw;
  },

  gamesFilters (state) {
    return state.filters;
  },

  gamesFilterCount: (state, getters) => (filterName) => {
    // Returns the number of enabled filters for the provided 'filterName'
    return state.filters[filterName].filter(function(item) { 
      return item.enabled;
    }).length;
  },

  gamesAllFiltersCount: (state) => {
    let all = state.filters.gameTypes.concat(state.filters.themes, state.filters.categories);

    return all.filter(function(item) { 
      return item.enabled;
    }).length;
  },

  gamesSearch (state) {
    return state.search;
  },

  gamesThemesMostPopular (state) {
    // Remove reactivity so we don't mutate the source
    let items = cleanSource(state.filters.themes); 

    // Sort based on number of plays, highest will be placed first in the array
    items.sort(function (a, b) {
      return b.themeStats.number_of_plays - a.themeStats.number_of_plays;
    });

    // Return the top 8 most popular themes
    return items.slice(0,8);
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};



// WEBPACK FOOTER //
// ./resources/assets/js/app/store/modules/games.js